import { Box, Button, Checkbox, Text } from '@factorialco/deprecated-design-system'
import { MeetingTasksListDocument } from 'generated/resources/graphql'
import { CreateOneOnOneTaskDocument, MeetingDetailFragment } from 'generated/resources/graphql'
import xor from 'lodash/xor'

import DayPicker from 'modules/core/components/DayPicker'
import FormRow from 'modules/core/components/FormRow'
import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface FormValues {
  name: string
  content: string
  dueOn: string
  assigneeIds: string[]
}

interface Props {
  onClose: () => void
  meeting: MeetingDetailFragment
}

export function NewTaskModal({ onClose, meeting }: Props) {
  const { employee } = useCurrentSession()
  const [createTask, {}] = useMutation(CreateOneOnOneTaskDocument, {
    extractorFn: (data) => data.oneOnOnesMutations.createOneononesTask,
  })

  const form = useForm<FormValues>({
    defaultValues: {
      name: '',
      content: '',
      dueOn: '',
      assigneeIds: [],
    },
    validate: (data) => {
      const errors: FormErrors<FormValues> = {}

      if (!data.dueOn) {
        errors.dueOn = i18n.t('common.validations.empty')
      }

      if (!data.name) {
        errors.name = i18n.t('common.validations.empty')
      }

      if (data.assigneeIds.length === 0) {
        errors.assigneeIds = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (data) => {
      const response = await createTask({
        variables: {
          meetingId: meeting.id,
          participantId: String(employee?.id),
          name: data.name,
          description: data.content,
          dueDate: dateUIToBackend(data.dueOn),
          assigneeIds: data.assigneeIds,
        },
        refetchQueries: [MeetingTasksListDocument],
      })

      const result = handleResult(response, () => {
        // TODO: do something with form errors
      })

      if (result.success) {
        track(trackingEvents.one_on_ones.addOneToOneTask)
        onClose()
      }

      return result
    },
  })

  const onPersonSelected = (assigneeId: string) => {
    form.setFieldValue('assigneeIds', xor(form.values.assigneeIds, [assigneeId]))
  }

  const firstParticipantId = String(meeting.oneOnOne.firstParticipant?.access.id)
  const secondParticipantId = String(meeting.oneOnOne.secondParticipant?.access.id)

  return (
    <Modal
      illustration="check"
      title={i18n.t('tasks.form.new.title')}
      description={i18n.t('tasks.form.new.description')}
      onClose={onClose}
      onSubmit={() => {
        form.handleSubmit()
      }}
      footer={
        <Button disabled={!form.isValid || form.isSubmitting} submit>
          {i18n.t('tasks.form.new.submit')}
        </Button>
      }
    >
      <FormRow>
        <TextField
          value={form.values.name}
          onChange={(value) => form.setFieldValue('name', value)}
          error={form.touched.name ? form.errors.name : undefined}
          label={i18n.t('tasks.labels.name')}
          mandatory
        />
      </FormRow>
      <FormRow>
        <Box gap="s4">
          <Text.Normal size="200" color="grey800" weight="medium">
            {i18n.t('tasks.labels.content')}
          </Text.Normal>
          <TextArea
            value={form.values.content}
            onChange={(value) => form.setFieldValue('content', value)}
            error={form.touched.content ? form.errors.content : undefined}
            placeholder=""
            minRows={5}
          />
        </Box>
      </FormRow>
      <FormRow>
        <Box gap="s4">
          <Text.Normal size="200" color="grey800" weight="medium">
            {i18n.t('tasks.labels.assignees')}
          </Text.Normal>
        </Box>

        <Box>
          <Checkbox
            checked={form.values.assigneeIds.includes(firstParticipantId)}
            onChange={() => onPersonSelected(firstParticipantId)}
            label={meeting.oneOnOne.firstParticipant?.fullName}
          />
          <Checkbox
            checked={form.values.assigneeIds.includes(secondParticipantId)}
            onChange={() => onPersonSelected(secondParticipantId)}
            label={meeting.oneOnOne.secondParticipant?.fullName}
          />
        </Box>
      </FormRow>
      <FormRow>
        <DayPicker
          label={i18n.t('tasks.labels.due_date')}
          placeholder="–"
          value={form.values.dueOn}
          onChange={(date) => form.setFieldValue('dueOn', date)}
          error={form.touched.dueOn ? form.errors.dueOn : undefined}
        />
      </FormRow>
    </Modal>
  )
}
